// OrderProblemBanner — Polish #14.
//
// Surfaces order-level problems proactively in the Command Center so Kimi
// sees the red flag BEFORE she starts typing a reply. The detector reads
// the primary (most recent) order from useOrderContext and fires on any
// of these signals:
//
//   refund        financialStatus = refunded | partially_refunded
//   cancelled     fulfillmentStatus / fulfillment.status contains
//                 "cancel" / "failure"
//   late          fulfillment is past its estimated delivery date and
//                 the status still looks in-transit (not delivered)
//   stuck         order is 5+ calendar days old and has no fulfillments
//                 yet (normal turnaround is 1-3 business days; a 5-day
//                 floor catches genuine delays without false-alarming
//                 weekend orders)
//   slow_transit  shipped 10+ days ago and still not marked delivered
//
// Banner is silent when:
//   - there are no orders
//   - the most recent order is healthy
//   - the most recent order is older than 60 days (ancient; not relevant)
//
// Visual: eggshell card with a clay dot, one-line headline, short why
// explanation, and a link to the order admin URL when we have one.

import { useMemo } from 'react';
import {
  useOrderContext,
  type OrderContextFulfillment,
  type OrderContextOrder,
} from '@/lib/hooks/useOrderContext';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Max age of the "primary order" we'll call out. Beyond this it's noise. */
const MAX_ORDER_AGE_DAYS = 60;

/** Turnaround floor before we flag as "stuck in production". */
const PRODUCTION_STUCK_DAYS = 5;

/** Shipped-but-not-delivered floor before we flag slow transit. */
const SLOW_TRANSIT_DAYS = 10;

type ProblemKind = 'refund' | 'cancelled' | 'late' | 'stuck' | 'slow_transit';

interface ProblemDetection {
  kind: ProblemKind;
  headline: string;
  detail: string;
  order: OrderContextOrder;
}

function hasCancelSignal(o: OrderContextOrder): boolean {
  const ff = (o.fulfillmentStatus ?? '').toLowerCase();
  if (ff.includes('cancel')) return true;
  for (const f of o.fulfillments ?? []) {
    const s = (f.status ?? '').toLowerCase();
    if (s.includes('cancel') || s.includes('failure')) return true;
  }
  return false;
}

function isRefunded(o: OrderContextOrder): boolean {
  const fs = (o.financialStatus ?? '').toLowerCase();
  return fs === 'refunded' || fs === 'partially_refunded';
}

/** Latest fulfillment that isn't delivered yet, if any. */
function inTransitFulfillment(o: OrderContextOrder): OrderContextFulfillment | null {
  const fulfillments = o.fulfillments ?? [];
  // Prefer the most recent fulfillment (array isn't guaranteed sorted so
  // walk it all). "Delivered" / "success" statuses are done — skip them.
  let latest: OrderContextFulfillment | null = null;
  for (const f of fulfillments) {
    const status = (f.status ?? '').toLowerCase();
    if (status.includes('deliver') || status === 'success') continue;
    if (status.includes('cancel') || status.includes('failure')) continue;
    if (!latest || new Date(f.createdAt).getTime() > new Date(latest.createdAt).getTime()) {
      latest = f;
    }
  }
  return latest;
}

/**
 * Inspect an order and return the first problem we detect, in priority
 * order (most recent / most customer-facing wins). Returns null when the
 * order looks healthy.
 */
function detectProblem(order: OrderContextOrder): ProblemDetection | null {
  const now = Date.now();
  const createdAt = new Date(order.createdAt).getTime();
  if (!Number.isFinite(createdAt)) return null;

  // Out of scope — ignore ancient orders so a refund from 2023 doesn't
  // shout on every future thread.
  if (now - createdAt > MAX_ORDER_AGE_DAYS * MS_PER_DAY) return null;

  // 1. Cancel signals beat everything else — the order won't ship.
  if (hasCancelSignal(order)) {
    return {
      kind: 'cancelled',
      headline: `${order.name} was cancelled.`,
      detail: 'Production was stopped. If the customer is asking about the order, this is why.',
      order,
    };
  }

  // 2. Refund on file. The customer may be asking about that very refund.
  if (isRefunded(order)) {
    const fs = (order.financialStatus ?? '').toLowerCase();
    const which = fs === 'partially_refunded' ? 'a partial refund' : 'a full refund';
    return {
      kind: 'refund',
      headline: `${order.name} has ${which} on file.`,
      detail:
        'Open the Shopify admin if you need the line items and amount. The refund is already posted, not pending.',
      order,
    };
  }

  const inTransit = inTransitFulfillment(order);

  // 3. Past its estimated delivery date.
  if (inTransit?.estimatedDeliveryAt) {
    const eta = new Date(inTransit.estimatedDeliveryAt).getTime();
    if (Number.isFinite(eta) && eta < now) {
      const daysLate = Math.max(1, Math.round((now - eta) / MS_PER_DAY));
      return {
        kind: 'late',
        headline: `${order.name} is ${daysLate} day${daysLate === 1 ? '' : 's'} past its estimated delivery.`,
        detail:
          'Carrier scans may lag, but if the customer is asking "where is my order?" the honest answer is that it is overdue.',
        order,
      };
    }
  }

  // 4. Slow transit — shipped a while back, still not delivered.
  if (inTransit) {
    const shippedAt = new Date(inTransit.createdAt).getTime();
    if (Number.isFinite(shippedAt) && now - shippedAt > SLOW_TRANSIT_DAYS * MS_PER_DAY) {
      const daysInTransit = Math.round((now - shippedAt) / MS_PER_DAY);
      return {
        kind: 'slow_transit',
        headline: `${order.name} has been in transit for ${daysInTransit} days.`,
        detail:
          'Most packages land in under a week. Worth pulling the tracking link and offering a reship if the carrier has gone silent.',
        order,
      };
    }
  }

  // 5. Stuck in production — no fulfillments, but past our normal turnaround.
  const hasFulfillments = (order.fulfillments ?? []).length > 0;
  const orderAgeDays = (now - createdAt) / MS_PER_DAY;
  if (!hasFulfillments && orderAgeDays >= PRODUCTION_STUCK_DAYS) {
    const days = Math.round(orderAgeDays);
    return {
      kind: 'stuck',
      headline: `${order.name} is ${days} days old with no shipment yet.`,
      detail:
        'Normal turnaround is 1 to 3 business days. Check production before promising a ship date.',
      order,
    };
  }

  return null;
}

export interface OrderProblemBannerProps {
  conversationId: string;
}

export function OrderProblemBanner({ conversationId }: OrderProblemBannerProps) {
  const { data } = useOrderContext(conversationId);

  const detection = useMemo<ProblemDetection | null>(() => {
    const orders = data?.orders ?? [];
    if (orders.length === 0) return null;
    // Only check the primary (most recent) order. Older orders rarely
    // drive a support thread, and flagging every order in a customer's
    // history would bury the signal.
    const primary = orders[0];
    if (!primary) return null;
    return detectProblem(primary);
  }, [data?.orders]);

  if (!detection) return null;

  return (
    <aside
      className={`order-problem-banner order-problem-banner--${detection.kind}`}
      role="status"
      aria-label="Order problem detected"
    >
      <span className="order-problem-dot" aria-hidden />
      <div className="order-problem-body">
        <div className="order-problem-headline">{detection.headline}</div>
        <div className="order-problem-detail">{detection.detail}</div>
      </div>
      {detection.order.adminUrl ? (
        <a
          className="order-problem-link"
          href={detection.order.adminUrl}
          target="_blank"
          rel="noreferrer"
        >
          Open order
        </a>
      ) : null}
    </aside>
  );
}
